/**
 * ScheduleReadinessCard — the per-lead "is this lead ready to schedule?" checklist
 * on the ContactRail "Next steps" tab. It renders the study's required
 * prescreening milestones and which are met: AUTO gates (interest, passed
 * prescreen, records received) show a live detected/not-yet state; MANUAL gates
 * (CRC review, investigator review, custom) are recruiter sign-offs toggled here
 * and stamped with who + when.
 *
 * Presentational: ContactDetail owns the fetch (so the header's schedule warning
 * stays in sync) and passes `readiness` + an `onToggle` that POSTs the sign-off.
 * Renders nothing when the lead's study set no requirements.
 */
import { useState } from 'react';
import { CalendarClock, Check, CheckCircle2, Circle, Loader2, Sparkles, UserCheck } from 'lucide-react';
import { ScheduleReadiness, ScheduleReadinessItem } from '../types';
import { useTeam } from '../lib';

interface Props {
  readiness: ScheduleReadiness | null;
  onToggle: (item: ScheduleReadinessItem, done: boolean) => Promise<void>;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function ScheduleReadinessCard({ readiness, onToggle }: Props) {
  const { byId: usersById } = useTeam();
  const [busyKey, setBusyKey] = useState<string | null>(null);

  if (!readiness || !readiness.configured || readiness.items.length === 0) return null;

  const { progress, allMet } = readiness;

  const toggle = async (item: ScheduleReadinessItem) => {
    if (busyKey) return;
    setBusyKey(item.key);
    try {
      await onToggle(item, !item.done);
    } finally {
      setBusyKey(null);
    }
  };

  const signerName = (id: string | null): string | null => {
    if (!id) return null;
    const u = usersById.get(id);
    return u?.name || u?.email || null;
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <CalendarClock className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Ready to schedule?
        </h3>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
            allMet
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
          }`}
        >
          {allMet ? <><Check className="h-3 w-3" /> All met</> : `${progress.done}/${progress.total} met`}
        </span>
      </div>

      <ul className="space-y-2">
        {readiness.items.map((item) => {
          const busy = busyKey === item.key;
          const who = signerName(item.doneBy);
          const on = fmtDate(item.doneAt);
          return (
            <li
              key={item.key}
              className={`rounded-lg border p-2.5 ${
                item.done
                  ? 'border-green-200 bg-green-50/50 dark:border-green-900/40 dark:bg-green-900/10'
                  : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
              }`}
            >
              <div className="flex items-start gap-2.5">
                <span className="mt-0.5 shrink-0">
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
                  ) : item.done ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  ) : (
                    <Circle className="h-4 w-4 text-gray-300 dark:text-gray-600" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className={`text-sm font-medium ${item.done ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                      {item.label}
                    </span>
                    {item.auto ? (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        <Sparkles className="h-2.5 w-2.5" /> {item.done ? 'Detected' : 'Auto'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                        <UserCheck className="h-2.5 w-2.5" /> Sign-off
                      </span>
                    )}
                  </div>

                  {/* Auto gates: a passive detected/not-yet line. */}
                  {item.auto && !item.done && (
                    <p className="mt-0.5 text-[11px] text-gray-400">Not detected yet — updates automatically.</p>
                  )}

                  {/* Manual gates: a sign-off toggle + who/when. */}
                  {!item.auto && (
                    <div className="mt-1">
                      {item.done ? (
                        <button
                          type="button"
                          onClick={() => toggle(item)}
                          disabled={busy}
                          title="Remove this sign-off"
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700 hover:underline disabled:opacity-50 dark:text-green-400"
                        >
                          Reviewed{who ? ` by ${who}` : ''}{on ? ` · ${on}` : ''} — undo
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => toggle(item)}
                          disabled={busy}
                          className="inline-flex min-h-[36px] items-center gap-1 rounded-md border border-primary-200 bg-white px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-primary-800 dark:bg-gray-800 dark:text-primary-300 dark:hover:bg-primary-900/30 sm:min-h-0"
                        >
                          <UserCheck className="h-3 w-3" /> Mark reviewed
                        </button>
                      )}
                      {item.note && (
                        <p className="mt-0.5 text-[11px] italic text-gray-400">“{item.note}”</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {!allMet && (
        <p className="mt-3 text-[11px] text-gray-400 dark:text-gray-500">
          Scheduling before these are met will ask you to confirm — it never blocks you.
        </p>
      )}
    </div>
  );
}
